import type { BMSDTO } from '@/models/dto/bms/bms-dto'
import type { BMSPaginationDTO } from '@/models/dto/bms/bms-pagination-dto'
import type { AggretationStrategy } from '@/models/types/plc/aggregation-strategy'
import type { Bms } from '@/models/types/bms/bms'
import type { BmsSensor } from '@/models/types/bms/bms-sensor'
import type { OrderType } from '@/types/table'
import { mapToBMS } from '@/models/mappers/bms/bms-mapper'
import { mapToBMSs } from '@/models/mappers/bms/bmss-mapper'
import { mapToBmsDTO } from '@/models/mappers/bms/bms-mapper'
import { capitalizeFirstLetter } from '@/utils/utils'
import type { BMSsWithPagination } from '@/models/types/bms/bms-with-pagination'
import type { FetchOptionsType } from './api'
import { getRequest, putJsonRequest} from './make-request'

type BMSGetOptions = {
    order?: OrderType
    sort?: keyof BMSDTO
    search?: string
    page?: string
} & Partial<Record<keyof Bms, string>>

type BMSSensorGetOptions = {
    from?: string
    to?: string
    strategy?: AggretationStrategy
    onlyLastValue?: boolean
} & Partial<Record<keyof BmsSensor, string>>

type BmsReading = {
    bid: number
    sid: number
    ts: string
    v: number
}

type BMSCrudType = (fetchOptions?: FetchOptionsType) => {
    getSensoriBmsCsv: (bmsId: number, options: BmsSensorsByRoomsOptions) => Promise<Blob>
    getSensoriBms: (
        bmsId: number,
        arg1: {
            idRooms: string[]
            deviceDescriptions: string[]
            timestampFrom: string | undefined
            timestampTo: string | undefined
        },
    ) => unknown
    getOne: (id: number) => Promise<Bms>
    getOneByFacilityId: (id: number) => Promise<Bms>
    getAll: () => Promise<Bms[]>
    getMany: (itemsPerPage?: string, options?: BMSGetOptions, onlyEnabled?: boolean) => Promise<BMSsWithPagination>
    getBmsSensorReadings: (bmsId: number, sensorId: number, options?: BMSSensorGetOptions) => Promise<BmsSensor>

    putAttributiPredefiniti: (bms: Bms) => Promise<Bms>
    putStanzePredefinite: (bms: Bms) => Promise<Bms>
}

type BmsSensorsByRoomsOptions = {
    idRooms?: string[]
    deviceDescriptions?: string[]
    timestampFrom?: string
    timestampTo?: string
}

const BMS_URL = typeof window === 'undefined' ? `${process.env.API_URL}/api/v1/BMS` : `${location.origin}/api/v1/BMS`
const BMS_TAG = 'BMS'

const bmsCRUD: BMSCrudType = (fetchOptions) => ({
    getOne: async (id) => {
        const url = new URL(`${BMS_URL}/${id.toString()}`)
        const bmsDTO = await getRequest<BMSDTO>({ url, next: { tags: [BMS_TAG] }, ...fetchOptions })
        console.log('prima map bmsDTO', bmsDTO.idImpiantoBMSAlias)
        const bms = mapToBMS(bmsDTO)
        console.log('dopo map', bms.bmsAliasId)
        return bms
    },
    getOneByFacilityId: async (id) => {
        const url = new URL(`${BMS_URL}/impianto/${id.toString()}`)
        const bmsDTO = await getRequest<BMSDTO>({ url, next: { tags: [BMS_TAG] }, ...fetchOptions })
        const bms = mapToBMS(bmsDTO)
        return bms
    },
    getAll: async () => {
        const url = new URL(BMS_URL)
        const bmsDTOs = await getRequest<BMSDTO[]>({ url, next: { tags: [BMS_TAG] }, ...fetchOptions })
        const bms = bmsDTOs.map(mapToBMS)
        return bms
    },
    getMany: async (itemsPerPage, options) => {
        const url = new URL(`${BMS_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')
        options?.page && url.searchParams.append('PageIndex', options.page)
        options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))

        options?.search && url.searchParams.append('Filters[search]', options.search)

        options?.name && url.searchParams.append('Filters[Nome]', options.name)

        const bmsPaginationDTO = await getRequest<BMSPaginationDTO>({
            url,
            next: { tags: [BMS_TAG] },
            ...fetchOptions,
        })

        const bmsPagination = mapToBMSs(bmsPaginationDTO)

        return bmsPagination
    },

    getBmsSensorReadings: async (bmsId, sensorId, options) => {
        const { from, to, strategy, onlyLastValue } = options || {}
        const url = new URL(`${BMS_URL}/${bmsId.toString()}/Sensore/${sensorId.toString()}`)
        from && url.searchParams.append('timestampFrom', from)
        to && url.searchParams.append('timestampTo', to)
        strategy && url.searchParams.append('aggregationStrategy', strategy.toString())
        onlyLastValue && url.searchParams.append('lastValue', 'true')

        const readings = await getRequest<BmsSensor>({
            url,
            next: { tags: [BMS_TAG] },
            ...fetchOptions,
        })
        return readings
    },

    // 2. Nel bmsCRUD definisci in modo esplicito che la funzione ritorna BmsReading[]
    getSensoriBms: async (bmsId: number, options: BmsSensorsByRoomsOptions): Promise<BmsReading[]> => {
        const { idRooms, deviceDescriptions, timestampFrom, timestampTo } = options
        const url = new URL(`${BMS_URL}/${bmsId.toString()}/SensoriBMS`)

        // Stanze multiple:
        idRooms?.forEach((room) => {
            url.searchParams.append('idRooms', room)
        })

        // ATTRIBUTI MULTIPLI (invece di un singolo deviceDescription)
        deviceDescriptions?.forEach((desc) => {
            url.searchParams.append('deviceDescription', desc)
        })

        if (timestampFrom) {
            url.searchParams.append('timestampFrom', timestampFrom)
        }
        if (timestampTo) {
            url.searchParams.append('timestampTo', timestampTo)
        }

        // console.log('INVIO RICHIESTA URL:', url.toString())

        // Aspettati un array di BmsReading
        const data = await getRequest<BmsReading[]>({
            url,
            next: { tags: [BMS_TAG] },
            ...fetchOptions,
        })
        // console.log('DATI DAL BACKEND:', data)
        return data
    },
    getSensoriBmsCsv: async (bmsId: number, options: BmsSensorsByRoomsOptions): Promise<Blob> => {
        const { idRooms, deviceDescriptions, timestampFrom, timestampTo } = options
        const url = new URL(`${BMS_URL}/${bmsId.toString()}/SensoreBMS/csv`)

        // Stanze multiple:
        idRooms?.forEach((room) => {
            url.searchParams.append('idRooms', room)
        })

        // ATTRIBUTI MULTIPLI
        deviceDescriptions?.forEach((desc) => {
            url.searchParams.append('deviceDescription', desc)
        })

        if (timestampFrom) {
            url.searchParams.append('timestampFrom', timestampFrom)
        }
        if (timestampTo) {
            url.searchParams.append('timestampTo', timestampTo)
        }

        console.log('URL generato per il download CSV:', url.toString())
        const response = await fetch(url.toString(), {
            method: 'GET',
            // headers: { Accept: 'text/csv' },
        })

        // console.log('RESPONSE CSV BMS:', response)

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status.toString()}`)
        }

        // Ritorno un Blob, così chi invoca questa funzione potrà occuparsi di salvare il file
        return await response.blob()
    },
     putAttributiPredefiniti: async (bms) => {
        const bmsDTO = mapToBmsDTO(bms)
        const url = new URL(`${BMS_URL}/AttributiPredefiniti`)
        const bmsDTOResponse = await putJsonRequest<BMSDTO>({ url, body: bmsDTO, ...fetchOptions })
        const updated = mapToBMS(bmsDTOResponse)
        return updated
    },

    putStanzePredefinite: async (bms) => {
        const bmsDTO = mapToBmsDTO(bms)
        const url = new URL(`${BMS_URL}/StanzePredefinite`)
        const bmsDTOResponse = await putJsonRequest<BMSDTO>({ url, body: bmsDTO, ...fetchOptions })
        const updated = mapToBMS(bmsDTOResponse)
        return updated
    },
})

export { BMS_TAG, bmsCRUD, type BMSCrudType, type BMSGetOptions }
